import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Url, USER_PROFILE_SECTION_PATHS } from '@wix/da-url';
import { PapiUser } from '@wix/da-papi-types';
import EmptyState from '../../EmptyState';
import { UserStream, Props as WidgetProps } from './UserStream';
import { ModuleType } from '../../../../types/modules';
import { inviteToGroup } from '../../../actions/groups';
import WidgetBox, {
  Props as WidgetBoxProps,
} from '../../UserWidgets/_partials/WidgetBox';
import UserStreamConfigPopover from './UserStreamConfigPopover';

import s from './UserStreamBox.scss';

const UserStreamBox: React.FC<
  WidgetProps &
    WidgetBoxProps & {
      type: ModuleType;
      totalCount?: number;
      isOwner: boolean;
      profileOwnerUser: PapiUser;
      canSeeAll?: boolean;
      withConfig?: boolean;
    }
> = ({
  type,
  isEmpty,
  isOwner,
  canSeeAll,
  totalCount,
  widgetRemove,
  widgetMoveUp,
  widgetMoveDown,
  widgetMenuRenderer,
  section,
  zone,
  withConfig,
  ...props
}) => {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const dispatchInviteAction = useCallback(
    () => dispatch(inviteToGroup(props.profileOwnerUser)),
    [dispatch, props.profileOwnerUser]
  );
  const onEmptyStateCTAClick = useCallback(() => {
    switch (type) {
      case ModuleType.GROUP_ADMINS:
      case ModuleType.GROUP_MEMBERS:
        return dispatchInviteAction();
      case ModuleType.WATCHERS:
      case ModuleType.ACTIVE_MEMBERS:
      default:
        return window.location.replace(Url.browseDeviantsYouWatchLink());
    }
  }, [type, dispatchInviteAction]);

  return (
    <WidgetBox
      section={section}
      zone={zone}
      header={t(`widgets.${type}.header`)}
      count={totalCount}
      seeAllUrl={Url.userLink(
        props.profileOwnerUser.username,
        `${USER_PROFILE_SECTION_PATHS.ABOUT}#${type}`
      )}
      hasMore={props.hasMore && canSeeAll}
      moduleId={props.moduleId}
      widgetMenuRenderer={
        withConfig
          ? close => (
              <UserStreamConfigPopover
                moduleId={props.moduleId}
                onClose={close}
              />
            )
          : undefined
      }
      widgetMoveUp={widgetMoveUp}
      widgetMoveDown={widgetMoveDown}
      widgetRemove={widgetRemove}
      isEmpty={isEmpty}
      emptyView={
        isOwner ? (
          <EmptyState
            view="small"
            className={s['empty-state']}
            title={t(`widgets.${type}.empty.title.owner`)}
            subtitle={t(`widgets.${type}.empty.subtitle.owner`)}
            withButton={true}
            buttonLabel={t(`widgets.${type}.empty.CTALabel.owner`)}
            onButtonClick={onEmptyStateCTAClick}
          />
        ) : null
      }
    >
      <UserStream {...props} />
    </WidgetBox>
  );
};
UserStreamBox.displayName = 'UserStreamBox';

export default UserStreamBox;
